export type EventSource = 'server' | 'client';

export type EventCategory =
  | 'connection' | 'entry' | 'lifecycle' | 'lock' | 'navigation' | 'popup' | 'presence';

type ActorType = 'Staffer' | 'Student';

export interface EventActor {
  id: number;
  name: string | null;
  type: ActorType;
}

export type EventMetadata = Record<string, unknown>;

export interface LiveSessionEvent {
  actor: EventActor | null;
  category: EventCategory;
  client_occurred_at: string | null;
  client_seq: number | null;
  event_type: string;
  id: number;
  metadata: EventMetadata;
  occurred_at: string;
  page_load_id: string | null;
  received_at: string;
  source: EventSource;
}

interface EventsMeta {
  has_more: boolean;
  max_id: number | null;
  next_cursor: string | null;
  server_time: string;
  session_status: 'active' | 'ended';
}

export interface EventsResponse {
  data: LiveSessionEvent[];
  meta: EventsMeta;
}

export interface EntryInfo {
  entry_mode: string | null;
  hydrated_session_id: string | null;
  nav_type: string | null;
  occurred_at: string;
  referrer: string | null;
  url: string | null;
  user_agent: string | null;
  via: string | null;
}

interface ParticipationStamps {
  first_joined_at: string;
  last_left_at: string | null;
  rejoin_count: number;
  removed_at: string | null;
}

interface HydrationResult {
  occurred_at: string;
  reason: string | null;
  result: string | null;
}

export interface ParticipantStudent {
  counts: {
    channel_rejected: number;
    events: number;
    nav_applied: number;
    ws_disconnected: number;
  };
  entry: {
    first_page_entered: EntryInfo | null;
    hydration_results: HydrationResult[];
    page_load_count: number;
  };
  full_name: string;
  id: number;
  initials: string;
  invited: boolean;
  last_event_at: string | null;
  participation: ParticipationStamps | null;
}

export interface ParticipantsResponse {
  students: ParticipantStudent[];
  teacher: {
    entry: EntryInfo | null;
    id: number;
    name: string | null;
  };
}

// `Staffer:12`, `Student:7`, or `system` for rows with no actor.
export type ActorKey = string;

export const SYSTEM_ACTOR: ActorKey = 'system';

export const actorKeyFor = (event: LiveSessionEvent): ActorKey => (
  event.actor ? `${event.actor.type}:${event.actor.id}` : SYSTEM_ACTOR
);

export const studentActorKey = (studentId: number): ActorKey => `Student:${studentId}`;

export interface Filters {
  actor: ActorKey | 'all';
  categories: EventCategory[];
  query: string;
  source: EventSource | 'all';
}

export const DEFAULT_FILTERS: Filters = {
  actor: 'all',
  categories: [],
  query: '',
  source: 'all',
};

export type AnalysisState = 'free' | 'healthy' | 'left_early' | 'never_joined' | 'stuck' | 'unknown';

export interface AnalysisVerdict {
  confidence: 'high' | 'low' | 'medium';
  evidence_event_ids: number[];
  explanation: string;
  mechanism: string;
  state: AnalysisState;
  student_id: number;
}

interface AnalysisReport {
  open_questions: string[];
  summary: string;
  teacher: { dropped_navs: number; drops: number; notes: string };
  verdicts: AnalysisVerdict[];
}

// GET .../analysis. The report holds real ids: the prompt only ever saw pseudonyms.
export interface LiveSessionAnalysis {
  events_max_id: number | null;
  generated_at: string;
  generated_by: string | null;
  id: number;
  model_identifier: string | null;
  newer_event_count: number;
  report: AnalysisReport;
}
